import type React from "react";
import styled, { css } from "styled-components";
import {
  Tab,
  Tooltip,
  Heading,
  HEADING_LEVELS,
  HEADING_TYPES,
} from "@cdkglobal/radial";
import type { HeadingProps } from "@cdkglobal/radial";
import { CustomStylingProps } from "../../types";
import { ComponentPropsWithoutRef } from "react";

const RecordPageSideSheetLayout = styled.div`
  overflow: auto;
  scrollbar-width: thin;
`;

const RecordPageDefaultLayout = styled.div`
  scrollbar-width: thin;
`;

const PrivlibSetupsRecordPageLayout = styled.div`
  overflow: auto;
  scrollbar-width: thin;
`;

const DashboardRecordPageLayout = styled.div`
  overflow: auto;
  scrollbar-width: thin;
`;

const RECORD_PAGE_CONTAINER_MAP: Record<
  string,
  | typeof RecordPageDefaultLayout
  | typeof RecordPageSideSheetLayout
  | typeof PrivlibSetupsRecordPageLayout
  | typeof DashboardRecordPageLayout
> = {
  "side-sheet-record-page-layout": RecordPageSideSheetLayout,
  "privlib-record-page-layout": PrivlibSetupsRecordPageLayout,
  "dashboard-record-page-layout": DashboardRecordPageLayout,
};

export const getRecordPageContainer = (
  $customStyle: string,
):
  | typeof RecordPageDefaultLayout
  | typeof RecordPageSideSheetLayout
  | typeof PrivlibSetupsRecordPageLayout
  | typeof DashboardRecordPageLayout => {
  return (
    RECORD_PAGE_CONTAINER_MAP[$customStyle ?? ""] ?? RecordPageDefaultLayout
  );
};

export const SetupInformation = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0rem 0rem 1.5rem 0.5rem;

  > strong,
  > b {
    margin: 0;
    line-height: 1.8;
  }
`;

// The wrapping heirarchy for the Record Page is
// 1) RecordPageMultiSectionLayout -> TOP Level - Setup level for managing the layout of all sections
// 2) RecordPageContent -> For managing the layout of each section (this may not be necessary)
// 3) RecordPageSectionLayout -> For managing the layout of each section
// 4) RecordPageFormLayout -> For managing the layout of the fields in a section
// 5) RecordPageFieldLayout -> For managing the layout of each field
// 6) DynamicFieldRenderer

export const RecordPageMultiSectionLayout = styled.div<CustomStylingProps>`
  ${({ $customStyle }) => {
    switch ($customStyle) {
      case "section-cards-column-layout":
        return css`
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(30rem, 1fr));
        `;

      default:
        return css``;
    }
  }}
`;

export const RecordPageContent = styled.div`
  display: contents;
`;

export const RecordPageSectionLayout = styled.div<CustomStylingProps>`
  padding: 1rem;

  ${({ $customStyle }) => {
    switch ($customStyle) {
      case "hidden":
        return css`
          display: none;
        `;

      case "inventory-building-details-section-layout":
        return css`
          margin: 0.25rem 0.5rem 1rem 0.5rem;
          padding: 0;
          border: none;
        `;

      case "inventory-building-zone-details-section-layout":
      case "inventory-building-route-details-section-layout":
        return css`
          margin: 1rem 0.5rem 1rem 1rem;
          padding: 0;
          border: none;
        `;

      default:
        return css``;
    }
  }}
`;

export const RecordSectionTitle = styled(
  Heading as React.ComponentType<HeadingProps & CustomStylingProps>,
).attrs({
  level: HEADING_LEVELS.LEVEL_2,
  type: HEADING_TYPES.SUBTITLE_2,
})<CustomStylingProps>`
  ${({ $customStyle }) => {
    switch ($customStyle) {
      case "hidden":
        return css`
          display: none;
        `;

      default:
        return css``;
    }
  }}
`;

export const RecordPageFormLayout = styled.div<CustomStylingProps>`
  display: grid;
  gap: 1rem;

  ${({ $customStyle }) => {
    switch ($customStyle) {
      case "section-fields-full-width":
        return css``;

      case "section-vertical":
        return css`
          display: grid;
          grid-template-columns: max-content;
        `;

      case "two-column-layout":
        return css`
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
        `;

      case "section-with-background":
        return css`
          display: flex;
          flex-wrap: wrap;
          align-items: flex-start;
          margin-bottom: 1rem;
          padding: 0.75rem;
          background: rgba(0, 0, 0, 0.06);
          border-radius: 0.5rem;
        `;

      case "section-horizontal":
        return css`
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
        `;

      case "section-stacked-two-column-layout":
        return css`
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
          gap: 1rem;
          align-items: start;

          > *:nth-child(1) {
            grid-column: 1 / -1;
          }

          @media (max-width: 900px) {
            grid-template-columns: 1fr;

            > * {
              grid-column: 1 / -1;
            }
          }
        `;

      case "inventory-building-details-form-layout":
        return css`
          grid-template-columns: repeat(12, minmax(0, 1fr));
          gap: 1rem;
          align-items: start;

          && > * {
            width: 100%;
            min-width: 0;
            max-width: none;
            justify-self: stretch;
          }

          > *:nth-child(1) {
            grid-column: 1 / span 2;
          }

          > *:nth-child(2) {
            grid-column: 3 / -1;
          }

          > *:nth-child(3) {
            grid-column: 1 / -1;
          }

          > *:nth-child(4) {
            grid-column: 1 / -1;
          }

          > *:nth-child(5) {
            grid-column: 1 / span 4;
          }

          > *:nth-child(6) {
            grid-column: 5 / span 4;
          }

          > *:nth-child(7) {
            grid-column: 9 / span 4;
            width: 100%;
            justify-self: stretch;
          }

          @media (max-width: 900px) {
            > *:nth-child(1) {
              grid-column: 1 / span 3;
            }

            > *:nth-child(2) {
              grid-column: 4 / -1;
            }

            > *:nth-child(5),
            > *:nth-child(6),
            > *:nth-child(7) {
              grid-column: 1 / -1;
            }
          }

          @media (max-width: 640px) {
            grid-template-columns: 1fr;

            > * {
              grid-column: 1 / -1 !important;
            }
          }
        `;

      default:
        return css`
          display: flex;
          flex-wrap: wrap;
          align-items: flex-start;
        `;
    }
  }}
`;

export const RecordPageFieldLayout = styled.div<CustomStylingProps>`
  ${({ $customStyle }) => {
    switch ($customStyle) {
      case "hidden":
        return css`
          display: none;
        `;

      case "field-size-compact":
        return css`
          width: auto;
          max-width: 12rem;
          justify-self: start;
          flex: 0 1 12rem;
        `;

      case "field-size-small":
        return css`
          width: auto;
          max-width: 18rem;
          justify-self: start;
          flex: 0 1 18rem;
        `;

      case "field-size-medium":
        return css`
          width: auto;
          max-width: 24rem;
          justify-self: start;
          flex: 0 1 24rem;
        `;

      case "field-size-wide":
        return css`
          width: auto;
          max-width: 30rem;
          justify-self: start;
          flex: 0 1 30rem;
        `;

      case "field-size-large":
        return css`
          width: auto;
          max-width: 44rem;
          justify-self: start;
          flex: 0 1 38rem;
        `;

      case "field-size-large-with-large-chip":
        return css`
          width: auto;
          max-width: 44rem;
          justify-self: start;
          flex: 0 1 38rem;

          [data-testid$="-value"] > div {
            max-width: 22rem;
          }
        `;

      case "field-size-full":
        return css`
          width: 100%;
          max-width: none;
          flex: 1 1 100%;
          grid-column: 1 / -1;
        `;

      case "field-with-button":
        return css`
          width: 100%;
          max-width: 35rem;
        `;

      case "grid-new-row":
        return css`
          grid-column: 1;
        `;

      default:
        return css`
          width: auto;
          max-width: 20rem;
          justify-self: start;
          flex: 0 1 18rem;
        `;
    }
  }}
`;

// CORELATION FORM STYLES
export const RowsContainer = styled.div``;

export const StyledCardRowWrapper = styled.div`
  margin-bottom: 1rem;

  background: #93939318;
  border-radius: 0.5rem;
  padding: 0.75rem 0.75rem;
`;

export const NormalRowWrapper = styled.div`
  margin-bottom: 1rem;
`;

export const InputsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 1rem;
`;

export const IconDeleteWrapper = styled.div`
  flex: 0 0 auto; /* delete btn */
`;

export const StyledTab = styled(
  Tab as React.ComponentType<ComponentPropsWithoutRef<typeof Tab>>,
)`
  ${({ isActive }) =>
    isActive &&
    `
      border: 2px solid #aecef7;
    `}
`;

export const InfoIconContainer = styled.div<CustomStylingProps>`
  ${({ $customStyle }) => {
    switch ($customStyle) {
      case "hidden":
        return css`
          display: none;
        `;

      default:
        return css`
          float: right;
        `;
    }
  }}
`;

export const RecordSectionHeader = styled.div<CustomStylingProps>`
  display: flex;
  align-items: self-end;
  gap: 0.5rem;
  margin-bottom: 0.75rem;

  ${({ $customStyle }) => {
    switch ($customStyle) {
      case "inventory-building-header":
        return css`
          ${RecordSectionTitle} {
            flex-shrink: 0; /* ← title never shrinks */
            white-space: nowrap;
          }

          > div:last-child {
            display: flex;
            align-items: center;
            flex-shrink: 0; /* ← switch never shrinks either */
          }
          margin-top: 1rem;
          align-items: baseline;
        `;
      default:
        return css``;
    }
  }}
`;

export const StyledTooltip = styled(Tooltip)`
  max-width: 20rem;
`;
